import { useState, useEffect } from 'react';
import axios from 'axios';
import { Save, Calculator, Bike, Car, ToggleLeft, ToggleRight, RefreshCw } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL;

const VEHICLE_LABELS = { bike: 'Moto / Bicicleta', car: 'Auto' };
const VEHICLE_ICONS  = { bike: Bike, car: Car };

function PricingCard({ config, onSave, saving }) {
  const [form, setForm] = useState({
    rate_per_km:  config.rate_per_km,
    minimum_fare: config.minimum_fare,
    minimum_km:   config.minimum_km,
    is_active:    config.is_active,
  });
  const [dirty, setDirty] = useState(false);

  function update(field, value) {
    setForm(f => ({ ...f, [field]: value }));
    setDirty(true);
  }

  function handleSave() {
    onSave(config.vehicle_type, form);
    setDirty(false);
  }

  const Icon = VEHICLE_ICONS[config.vehicle_type];

  return (
    <div className={`bg-white rounded-xl border p-5 space-y-4 ${form.is_active ? 'border-gray-200' : 'border-gray-100 opacity-70'}`}>
      {/* Card header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 font-semibold text-gray-800">
          <Icon size={18} className="text-brand-primary" />
          {VEHICLE_LABELS[config.vehicle_type]}
        </div>
        <button
          onClick={() => update('is_active', !form.is_active)}
          className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-brand-primary transition-colors"
        >
          {form.is_active
            ? <ToggleRight size={20} className="text-brand-green" />
            : <ToggleLeft  size={20} className="text-gray-400" />
          }
          {form.is_active ? 'Activo' : 'Inactivo'}
        </button>
      </div>

      {/* Fields */}
      <div className="grid grid-cols-3 gap-4">
        <div className="space-y-1">
          <label className="text-xs text-gray-400">Tarifa por km</label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">S/</span>
            <input
              type="number"
              step="0.10"
              min="0"
              value={form.rate_per_km}
              onChange={e => update('rate_per_km', parseFloat(e.target.value) || 0)}
              className="w-full border border-gray-200 rounded-lg pl-8 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/30"
            />
          </div>
        </div>

        <div className="space-y-1">
          <label className="text-xs text-gray-400">Tarifa mínima</label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">S/</span>
            <input
              type="number"
              step="0.50"
              min="0"
              value={form.minimum_fare}
              onChange={e => update('minimum_fare', parseFloat(e.target.value) || 0)}
              className="w-full border border-gray-200 rounded-lg pl-8 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/30"
            />
          </div>
        </div>

        <div className="space-y-1">
          <label className="text-xs text-gray-400">Km mínimos</label>
          <input
            type="number"
            step="0.5"
            min="0"
            value={form.minimum_km}
            onChange={e => update('minimum_km', parseFloat(e.target.value) || 0)}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/30"
          />
        </div>
      </div>

      {/* Earnings breakdown preview */}
      <div className="bg-gray-50 rounded-lg px-4 py-3 text-xs text-gray-500 space-y-1">
        <div className="flex justify-between">
          <span>Tarifa mínima cobrada:</span>
          <span className="font-medium text-gray-700">S/ {Number(form.minimum_fare).toFixed(2)}</span>
        </div>
        <div className="flex justify-between">
          <span>Fast Max recibe (25%):</span>
          <span className="font-medium text-brand-primary">S/ {(form.minimum_fare * 0.25).toFixed(2)}</span>
        </div>
        <div className="flex justify-between">
          <span>Conductor recibe (75%):</span>
          <span className="font-medium text-brand-green">S/ {(form.minimum_fare * 0.75).toFixed(2)}</span>
        </div>
        {config.updated_at && (
          <div className="pt-1 text-gray-400">
            Última actualización: {new Date(config.updated_at).toLocaleString('es-PE')}
          </div>
        )}
      </div>

      {/* Save */}
      {dirty && (
        <button
          onClick={handleSave}
          disabled={saving}
          className="w-full flex items-center justify-center gap-2 bg-brand-primary text-white py-2 rounded-lg text-sm font-medium hover:bg-brand-primary/90 transition-colors disabled:opacity-60"
        >
          <Save size={14} />
          {saving ? 'Guardando...' : 'Guardar cambios'}
        </button>
      )}
    </div>
  );
}

function PriceSimulator({ configs }) {
  const [distance, setDistance]   = useState('');
  const [vehicle, setVehicle]     = useState('bike');

  const config = configs.find(c => c.vehicle_type === vehicle);
  let quote = null;
  if (config && distance && !isNaN(parseFloat(distance))) {
    const km  = parseFloat(distance);
    const raw = km * config.rate_per_km;
    const total = Math.max(raw, config.minimum_fare);
    quote = {
      total:          total.toFixed(2),
      driver_earns:   (total * 0.75).toFixed(2),
      fastmax_earns:  (total * 0.25).toFixed(2),
      applied_min:    raw < config.minimum_fare,
    };
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
      <div className="flex items-center gap-2 font-semibold text-gray-800">
        <Calculator size={16} className="text-brand-gold" />
        Simulador de precio
      </div>

      <div className="flex gap-3">
        <div className="flex flex-col gap-1 flex-1">
          <label className="text-xs text-gray-400">Distancia (km)</label>
          <input
            type="number"
            step="0.1"
            min="0"
            placeholder="ej. 7.5"
            value={distance}
            onChange={e => setDistance(e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/30"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs text-gray-400">Vehículo</label>
          <select
            value={vehicle}
            onChange={e => setVehicle(e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/30 bg-white"
          >
            <option value="bike">🚴 Moto/Bici</option>
            <option value="car">🚗 Auto</option>
          </select>
        </div>
      </div>

      {quote ? (
        <div className="bg-gray-50 rounded-xl p-4 space-y-2">
          {quote.applied_min && (
            <div className="text-xs text-yellow-600 font-medium">⚠ Tarifa mínima aplicada</div>
          )}
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">Total cobrado al cliente:</span>
            <span className="font-bold text-gray-800 text-base">S/ {quote.total}</span>
          </div>
          <div className="flex justify-between text-xs">
            <span className="text-gray-400">Fast Max (25%):</span>
            <span className="font-semibold text-brand-primary">S/ {quote.fastmax_earns}</span>
          </div>
          <div className="flex justify-between text-xs">
            <span className="text-gray-400">Conductor (75%):</span>
            <span className="font-semibold text-brand-green">S/ {quote.driver_earns}</span>
          </div>
        </div>
      ) : (
        <div className="text-xs text-gray-400 text-center py-4">
          Ingresa una distancia para ver el precio estimado
        </div>
      )}
    </div>
  );
}

export default function ExpressPricingTab() {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [saved, setSaved]     = useState('');

  useEffect(() => {
    async function fetchPricing() {
      try {
        const { data } = await axios.get(`${API_URL}/api/express/pricing`);
        setConfigs(data);
      } catch (err) {
        console.error('Error fetching pricing:', err);
      } finally {
        setLoading(false);
      }
    }
    fetchPricing();
  }, []);

  async function handleSave(vehicleType, form) {
    setSaving(true);
    try {
      const { data } = await axios.patch(`${API_URL}/api/express/pricing/${vehicleType}`, form);
      setConfigs(prev => prev.map(c => c.vehicle_type === vehicleType ? { ...c, ...data } : c));
      setSaved(vehicleType);
      setTimeout(() => setSaved(''), 2500);
    } catch (err) {
      console.error('Error saving pricing:', err);
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div className="text-center py-12 text-gray-400 text-sm">Cargando precios...</div>;

  return (
    <div className="space-y-4">
      {saved && (
        <div className="bg-green-50 border border-green-200 text-green-700 text-sm rounded-xl px-4 py-2.5">
          ✓ Precios de {VEHICLE_LABELS[saved]} actualizados correctamente
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {configs.map(config => (
          <PricingCard
            key={config.vehicle_type}
            config={config}
            onSave={handleSave}
            saving={saving}
          />
        ))}
      </div>

      <PriceSimulator configs={configs} />
    </div>
  );
}